import type {
  AiContextPreview,
  AiConversation,
  AiMessageResponse,
  AiMode,
  AiStatus,
  AiStoredMessage,
  Chapter,
  CharacterMention,
  AudiobookState,
  AudiobookTiming,
  CollectionItem,
  ReaderBook,
  ReviewAnnotation,
  ReviewAnchor,
  ReviewCategory,
  SceneReviewStatus,
  SceneReviewStatusEntry,
  ContinuityView,
  DashboardData,
  ExportFormat,
  ImportApplyResult,
  ImportPreview,
  ProjectImportItem,
  LibraryBook,
  Manuscript,
  Part,
  Project,
  ProjectInsights,
  ManuscriptPolishReport,
  IntelligenceDashboard,
  CharacterIntel,
  CharacterArc,
  LocationIntel,
  LocationContext,
  ThreadIntel,
  ReviewItem,
  CleanupSummary,
  RevisionPass,
  RevisionTask,
  RevisionPassStatus,
  RevisionTaskStatus,
  RevisionTaskCategory,
  PromisesResult,
  PromiseView,
  PromiseType,
  PromiseStatus,
  AskEntry,
  CacheStatus,
  CacheBuildResult,
  StoryOverview,
  ChapterSummaryView,
  EventLedger,
  IntelStatus,
  CanonFact,
  CanonCandidate,
  CanonCategory,
  PacingResult,
  PublishMeta,
  PublishValidation,
  BetaResult,
  SnapshotMeta,
  SnapshotDiff,
  ProjectSettings,
  RelationshipKind,
  RelationshipsView,
  Revision,
  RevisionSummary,
  Scene,
  SearchResult,
  FindResult,
  ReplaceResult,
  FindReplaceOptions,
  WritingStats,
  SessionUser,
  VaultItem
} from "./types.js";

export interface ExtractionCounts {
  characters: number;
  locations: number;
  storyBible: number;
  timeline: number;
  plotThreads: number;
}

export interface FailedChunkInfo {
  index: number;
  error: string;
}

/** Mirrors the API's extraction_jobs row — one background "Auto-populate" run. */
export interface ExtractionJob {
  id: string;
  projectId: string;
  status: "running" | "done" | "error";
  totalChunks: number;
  doneChunks: number;
  cachedChunks: number;
  failedChunks: number;
  totalWords: number;
  doneWords: number;
  createdCounts: ExtractionCounts | null;
  updatedCounts: ExtractionCounts | null;
  failed: FailedChunkInfo[];
  error: string | null;
  createdAt: string;
  updatedAt: string;
  finishedAt: string | null;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

export async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: "same-origin",
    headers:
      options.body !== undefined
        ? { "Content-Type": "application/json", ...(options.headers ?? {}) }
        : options.headers,
    ...options
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const data = text ? JSON.parse(text) : {};

  if (!response.ok) {
    const message =
      (data && typeof data.message === "string" && data.message) ||
      `Request failed (${response.status})`;
    throw new ApiError(response.status, message);
  }
  return data as T;
}

/** Optional per-action model override sent with an AI request. */
export type ModelChoice = { provider?: string | null; model?: string | null };

export const api = {
  // ---- Auth ----
  session(): Promise<{ authenticated: boolean; user?: SessionUser; expiresAt?: number }> {
    return request("/auth/session");
  },
  login(username: string, password: string): Promise<{ success: boolean; user?: SessionUser; message?: string }> {
    return request("/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password })
    });
  },
  logout(): Promise<{ success: boolean }> {
    return request("/auth/logout", { method: "POST" });
  },
  changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    return request("/account/password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword })
    });
  },

  // ---- Projects ----
  listProjects(): Promise<{ projects: Project[] }> {
    return request("/projects");
  },
  library(): Promise<{ books: LibraryBook[] }> {
    return request("/projects/library");
  },
  createProject(input: {
    type?: "manuscript" | "coloring";
    title: string;
    subtitle?: string;
    author?: string;
    description?: string;
    genre?: string;
    coloring?: {
      audience?: string;
      difficulty?: string;
      pageCount?: number;
      trim?: string;
      trimWidth?: number;
      trimHeight?: number;
      concept?: string;
      brief?: string;
    };
  }): Promise<{ project: Project }> {
    return request("/projects", { method: "POST", body: JSON.stringify(input) });
  },
  getProject(id: string): Promise<{ project: Project; settings: ProjectSettings }> {
    return request(`/projects/${id}`);
  },
  updateProject(id: string, input: Partial<Project>): Promise<{ project: Project }> {
    return request(`/projects/${id}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  updateSettings(id: string, input: Partial<ProjectSettings>): Promise<{ settings: ProjectSettings }> {
    return request(`/projects/${id}/settings`, { method: "PATCH", body: JSON.stringify(input) });
  },
  deleteProject(id: string): Promise<{ success: boolean }> {
    return request(`/projects/${id}`, { method: "DELETE" });
  },
  dashboard(id: string): Promise<DashboardData> {
    return request(`/projects/${id}/dashboard`);
  },

  // ---- Manuscript ----
  manuscript(projectId: string): Promise<Manuscript> {
    return request(`/projects/${projectId}/manuscript`);
  },
  createPart(projectId: string, title: string): Promise<{ part: Part }> {
    return request(`/projects/${projectId}/parts`, { method: "POST", body: JSON.stringify({ title }) });
  },
  renamePart(id: string, title: string): Promise<{ part: Part }> {
    return request(`/parts/${id}`, { method: "PATCH", body: JSON.stringify({ title }) });
  },
  deletePart(id: string): Promise<{ success: boolean }> {
    return request(`/parts/${id}`, { method: "DELETE" });
  },
  createChapter(projectId: string, title: string, partId?: string | null): Promise<{ chapter: Chapter }> {
    return request(`/projects/${projectId}/chapters`, {
      method: "POST",
      body: JSON.stringify({ title, partId: partId ?? null })
    });
  },
  updateChapter(id: string, input: { title?: string; synopsis?: string | null; partId?: string | null }): Promise<{ chapter: Chapter }> {
    return request(`/chapters/${id}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  deleteChapter(id: string): Promise<{ success: boolean }> {
    return request(`/chapters/${id}`, { method: "DELETE" });
  },
  createScene(chapterId: string, title: string): Promise<{ scene: Scene }> {
    return request(`/chapters/${chapterId}/scenes`, { method: "POST", body: JSON.stringify({ title }) });
  },
  getScene(id: string): Promise<{ scene: Scene }> {
    return request(`/scenes/${id}`);
  },
  saveScene(id: string, content: string, reason: "autosave" | "manual" = "autosave"): Promise<{
    scene: { id: string; wordCount: number; charCount: number; updatedAt: string };
    revisionCreated: boolean;
    savedAt: string;
  }> {
    return request(`/scenes/${id}/content`, {
      method: "PUT",
      body: JSON.stringify({ content, reason })
    });
  },
  updateScene(id: string, input: Partial<Scene>): Promise<{ scene: Scene }> {
    return request(`/scenes/${id}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  deleteScene(id: string): Promise<{ success: boolean }> {
    return request(`/scenes/${id}`, { method: "DELETE" });
  },
  reorderChapters(projectId: string, chapters: { id: string; partId: string | null }[]): Promise<{ success: boolean }> {
    return request(`/projects/${projectId}/chapter-order`, {
      method: "PUT",
      body: JSON.stringify({ chapters })
    });
  },
  reorderScenes(projectId: string, chapters: { chapterId: string; sceneIds: string[] }[]): Promise<{ success: boolean }> {
    return request(`/projects/${projectId}/scene-order`, {
      method: "PUT",
      body: JSON.stringify({ chapters })
    });
  },

  // ---- Revisions ----
  listRevisions(sceneId: string): Promise<{ revisions: RevisionSummary[] }> {
    return request(`/scenes/${sceneId}/revisions`);
  },
  getRevision(sceneId: string, revisionId: string): Promise<{ revision: Revision }> {
    return request(`/scenes/${sceneId}/revisions/${revisionId}`);
  },
  restoreRevision(sceneId: string, revisionId: string): Promise<{ scene: Scene }> {
    return request(`/scenes/${sceneId}/revisions/${revisionId}/restore`, { method: "POST" });
  },

  // Vault (soft-delete on manuscript delete).
  deleteScene2(id: string, toVault: boolean): Promise<{ success: boolean; vaulted: boolean }> {
    return request(`/scenes/${id}${toVault ? "?vault=true" : ""}`, { method: "DELETE" });
  },
  deleteChapter2(id: string, toVault: boolean): Promise<{ success: boolean; vaulted: boolean }> {
    return request(`/chapters/${id}${toVault ? "?vault=true" : ""}`, { method: "DELETE" });
  },

  // ---- Phase 2 collections (generic) ----
  listCollection(projectId: string, path: string): Promise<{ items: CollectionItem[] }> {
    return request(`/projects/${projectId}/${path}`);
  },
  createCollectionItem(projectId: string, path: string, data: Record<string, unknown>): Promise<{ item: CollectionItem }> {
    return request(`/projects/${projectId}/${path}`, { method: "POST", body: JSON.stringify(data) });
  },
  getCollectionItem(path: string, id: string): Promise<{ item: CollectionItem }> {
    return request(`/${path}/${id}`);
  },
  updateCollectionItem(path: string, id: string, data: Record<string, unknown>): Promise<{ item: CollectionItem }> {
    return request(`/${path}/${id}`, { method: "PATCH", body: JSON.stringify(data) });
  },
  deleteCollectionItem(path: string, id: string): Promise<{ success: boolean }> {
    return request(`/${path}/${id}`, { method: "DELETE" });
  },
  characterMentions(id: string): Promise<{ mentions: CharacterMention[] }> {
    return request(`/characters/${id}/mentions`);
  },
  locationContext(id: string): Promise<LocationContext> {
    return request(`/locations/${id}/context`);
  },
  reorderTimeline(projectId: string, ids: string[]): Promise<{ success: boolean }> {
    return request(`/projects/${projectId}/timeline-order`, { method: "PUT", body: JSON.stringify({ ids }) });
  },
  duplicateCandidates(projectId: string, kind: string): Promise<{ groups: { items: { id: string; name: string }[] }[] }> {
    return request(`/projects/${projectId}/collections/${kind}/duplicates`);
  },
  mergeCollection(projectId: string, kind: string, keepId: string, mergeIds: string[]): Promise<{ result: { merged: number; keepId: string } }> {
    return request(`/projects/${projectId}/collections/${kind}/merge`, {
      method: "POST",
      body: JSON.stringify({ keepId, mergeIds })
    });
  },

  // ---- Vault ----
  listVault(projectId: string): Promise<{ items: VaultItem[] }> {
    return request(`/projects/${projectId}/vault`);
  },
  getVaultItem(id: string): Promise<{ item: VaultItem }> {
    return request(`/vault/${id}`);
  },
  addVaultText(projectId: string, title: string, content: string): Promise<{ item: VaultItem }> {
    return request(`/projects/${projectId}/vault`, { method: "POST", body: JSON.stringify({ title, content }) });
  },
  restoreVaultItem(id: string): Promise<{ success: boolean; kind: string; sceneId?: string }> {
    return request(`/vault/${id}/restore`, { method: "POST" });
  },
  deleteVaultItem(id: string): Promise<{ success: boolean }> {
    return request(`/vault/${id}`, { method: "DELETE" });
  },

  // ---- Search ----
  search(projectId: string, query: string): Promise<{ results: SearchResult[]; query: string }> {
    return request(`/projects/${projectId}/search?q=${encodeURIComponent(query)}`);
  },

  // ---- Find & replace (manuscript-wide) ----
  findInProject(
    projectId: string,
    query: string,
    opts: FindReplaceOptions = {}
  ): Promise<FindResult> {
    return request(`/projects/${projectId}/find`, {
      method: "POST",
      body: JSON.stringify({ query, ...opts })
    });
  },
  replaceInProject(
    projectId: string,
    input: { query: string; replacement: string; sceneIds?: string[] } & FindReplaceOptions
  ): Promise<ReplaceResult> {
    return request(`/projects/${projectId}/replace`, {
      method: "POST",
      body: JSON.stringify(input)
    });
  },

  // ---- Writing goals & streaks ----
  writingStats(projectId: string): Promise<WritingStats> {
    return request(`/projects/${projectId}/writing-stats`);
  },
  ttsStatus(projectId: string): Promise<{
    available: boolean;
    provider: string | null;
    providerLabel: string;
    voices: string[];
    defaultVoice: string;
    providers: { id: string; label: string; reachable: boolean }[];
  }> {
    return request(`/projects/${projectId}/tts/status`);
  },

  // ---- Scribe AI ----
  aiStatus(projectId: string): Promise<AiStatus> {
    return request(`/projects/${projectId}/ai/status`);
  },
  blueprintStatus(projectId: string): Promise<{ configured: boolean; status: "connected" | "auth-failed" | "unreachable" | "ollama-unavailable"; detail: string; model?: string }> {
    return request(`/projects/${projectId}/ai/blueprint/status`);
  },
  blueprintModels(projectId: string): Promise<{ models: { name: string; size: number | null }[]; configuredModel: string; configuredModelInstalled: boolean }> {
    return request(`/projects/${projectId}/ai/blueprint/models`);
  },
  togetherModels(projectId: string): Promise<{ models: string[]; configuredModel: string; configuredModelAvailable: boolean }> {
    return request(`/projects/${projectId}/ai/together/models`);
  },
  aiModels(projectId: string, task: string): Promise<import("./types.js").ModelCatalogResponse> {
    return request(`/projects/${projectId}/ai/models?task=${encodeURIComponent(task)}`);
  },
  aiContext(
    projectId: string,
    body: { mode: AiMode; sceneId?: string | null; selectedText?: string | null; excludedIds?: string[]; chapterScope?: boolean }
  ): Promise<AiContextPreview> {
    return request(`/projects/${projectId}/ai/context`, { method: "POST", body: JSON.stringify(body) });
  },
  aiMessage(
    projectId: string,
    body: {
      mode: AiMode;
      conversationId?: string | null;
      sceneId?: string | null;
      selectedText?: string | null;
      message?: string | null;
      critiqueTarget?: "selection" | "scene" | "chapter" | null;
      chapterScope?: boolean;
      excludedIds?: string[];
      provider?: string | null;
      model?: string | null;
    }
  ): Promise<AiMessageResponse> {
    return request(`/projects/${projectId}/ai/message`, { method: "POST", body: JSON.stringify(body) });
  },
  aiConversations(projectId: string): Promise<{ conversations: AiConversation[] }> {
    return request(`/projects/${projectId}/ai/conversations`);
  },
  aiConversation(id: string): Promise<{ conversation: AiConversation; messages: AiStoredMessage[] }> {
    return request(`/ai/conversations/${id}`);
  },
  aiDeleteConversation(id: string): Promise<{ success: boolean }> {
    return request(`/ai/conversations/${id}`, { method: "DELETE" });
  },

  // ---- Manuscript intelligence ----
  insights(projectId: string): Promise<ProjectInsights> {
    return request(`/projects/${projectId}/intelligence`);
  },
  manuscriptPolish(projectId: string): Promise<ManuscriptPolishReport> {
    return request(`/projects/${projectId}/polish`);
  },

  // ---- Story Intelligence: derived views, review queue, cleanup ----
  intelDashboard(projectId: string): Promise<IntelligenceDashboard> {
    return request(`/projects/${projectId}/intelligence/dashboard`);
  },
  intelCharacters(projectId: string): Promise<{ characters: CharacterIntel[] }> {
    return request(`/projects/${projectId}/intelligence/characters`);
  },
  intelLocations(projectId: string): Promise<{ locations: LocationIntel[] }> {
    return request(`/projects/${projectId}/intelligence/locations`);
  },
  intelThreads(projectId: string): Promise<{ threads: ThreadIntel[] }> {
    return request(`/projects/${projectId}/intelligence/threads`);
  },
  characterArc(projectId: string, characterId: string): Promise<CharacterArc> {
    return request(`/projects/${projectId}/intelligence/characters/${characterId}/arc`);
  },
  reviewQueue(projectId: string, status = "open"): Promise<{ items: ReviewItem[]; openCount: number }> {
    return request(`/projects/${projectId}/intelligence/review?status=${encodeURIComponent(status)}`);
  },
  resolveReview(projectId: string, reviewId: string, body: { action?: string; keepId?: string } = {}): Promise<{ applied: string; openCount: number }> {
    return request(`/projects/${projectId}/intelligence/review/${reviewId}/resolve`, { method: "POST", body: JSON.stringify(body) });
  },
  dismissReview(projectId: string, reviewId: string): Promise<{ openCount: number }> {
    return request(`/projects/${projectId}/intelligence/review/${reviewId}/dismiss`, { method: "POST", body: JSON.stringify({}) });
  },
  runCleanup(projectId: string, autoApplySafe = false): Promise<CleanupSummary> {
    return request(`/projects/${projectId}/intelligence/cleanup`, { method: "POST", body: JSON.stringify({ autoApplySafe }) });
  },

  // ---- Beta-Reader Simulation ----
  betaReader(projectId: string, chapterId: string, profile: string, customNote?: string, model?: ModelChoice): Promise<BetaResult> {
    return request(`/projects/${projectId}/beta`, { method: "POST", body: JSON.stringify({ chapterId, profile, customNote, ...(model ?? {}) }) });
  },

  // ---- Publishing ----
  publishMeta(projectId: string): Promise<{ meta: PublishMeta }> {
    return request(`/projects/${projectId}/publish`);
  },
  updatePublishMeta(projectId: string, patch: Partial<PublishMeta>): Promise<{ meta: PublishMeta }> {
    return request(`/projects/${projectId}/publish`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  validatePublish(projectId: string): Promise<PublishValidation> {
    return request(`/projects/${projectId}/publish/validate`);
  },

  // ---- Pacing / Tension ----
  pacing(projectId: string): Promise<PacingResult> {
    return request(`/projects/${projectId}/pacing`);
  },

  // ---- Draft Comparison ----
  snapshots(projectId: string): Promise<{ snapshots: SnapshotMeta[] }> {
    return request(`/projects/${projectId}/snapshots`);
  },
  createSnapshot(projectId: string, label: string): Promise<{ snapshot: SnapshotMeta }> {
    return request(`/projects/${projectId}/snapshots`, { method: "POST", body: JSON.stringify({ label }) });
  },
  deleteSnapshot(projectId: string, id: string): Promise<{ success: boolean }> {
    return request(`/projects/${projectId}/snapshots/${id}`, { method: "DELETE" });
  },
  compareSnapshots(projectId: string, a: string, b: string): Promise<SnapshotDiff> {
    return request(`/projects/${projectId}/snapshots/compare?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`);
  },

  // ---- Canon Locks ----
  canon(projectId: string): Promise<{ facts: CanonFact[]; suggestions: CanonCandidate[] }> {
    return request(`/projects/${projectId}/canon`);
  },
  createCanon(projectId: string, body: { fact: string; category?: CanonCategory; subject?: string | null; scopeLabel?: string | null; effectiveFromSceneId?: string | null; effectiveUntilSceneId?: string | null }): Promise<{ fact: CanonFact }> {
    return request(`/projects/${projectId}/canon`, { method: "POST", body: JSON.stringify(body) });
  },
  updateCanon(projectId: string, factId: string, body: { fact?: string; category?: CanonCategory; subject?: string | null; scopeLabel?: string | null; effectiveFromSceneId?: string | null; effectiveUntilSceneId?: string | null; locked?: number }): Promise<{ fact: CanonFact }> {
    return request(`/projects/${projectId}/canon/${factId}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  deleteCanon(projectId: string, factId: string): Promise<{ success: boolean }> {
    return request(`/projects/${projectId}/canon/${factId}`, { method: "DELETE" });
  },
  checkCanon(projectId: string, model?: ModelChoice): Promise<{ result: { conflicts: number; chunks: number }; reviewOpen: number }> {
    return request(`/projects/${projectId}/canon/check`, { method: "POST", body: JSON.stringify(model ?? {}) });
  },

  // ---- Ask the Book (persisted history) ----
  askBook(projectId: string, question: string, model?: ModelChoice): Promise<{ entry: AskEntry }> {
    return request(`/projects/${projectId}/ask`, { method: "POST", body: JSON.stringify({ question, ...(model ?? {}) }) });
  },
  listAsk(projectId: string): Promise<{ entries: AskEntry[] }> {
    return request(`/projects/${projectId}/ask`);
  },
  reask(projectId: string, askId: string, model?: ModelChoice): Promise<{ entry: AskEntry }> {
    return request(`/projects/${projectId}/ask/${askId}/reask`, { method: "POST", body: JSON.stringify(model ?? {}) });
  },
  deleteAsk(projectId: string, askId: string): Promise<{ ok: boolean }> {
    return request(`/projects/${projectId}/ask/${askId}`, { method: "DELETE" });
  },

  // ---- Book / chapter overview ----
  storyOverview(projectId: string): Promise<{ overview: StoryOverview | null; chapters: ChapterSummaryView[] }> {
    return request(`/projects/${projectId}/story-overview`);
  },
  eventLedger(projectId: string): Promise<EventLedger> {
    return request(`/projects/${projectId}/ledger`);
  },
  organizeCharacters(projectId: string, model?: ModelChoice): Promise<{ sectionsCreated: number; sectionsReused: number; assigned: number }> {
    return request(`/projects/${projectId}/characters/organize`, { method: "POST", body: JSON.stringify(model ?? {}) });
  },
  reorderCharacterGroups(projectId: string, ids: string[]): Promise<{ success: boolean }> {
    return request(`/projects/${projectId}/character-groups-order`, { method: "PUT", body: JSON.stringify({ ids }) });
  },
  reorderCharacters(projectId: string, ids: string[]): Promise<{ success: boolean }> {
    return request(`/projects/${projectId}/characters-order`, { method: "PUT", body: JSON.stringify({ ids }) });
  },

  // ---- Book Cache ----
  cacheStatus(projectId: string): Promise<{ status: CacheStatus }> {
    return request(`/projects/${projectId}/cache`);
  },
  buildCache(projectId: string, model?: ModelChoice): Promise<CacheBuildResult> {
    return request(`/projects/${projectId}/cache/build`, { method: "POST", body: JSON.stringify(model ?? {}) });
  },

  // ---- Story Promises ----
  promises(projectId: string): Promise<PromisesResult> {
    return request(`/projects/${projectId}/promises`);
  },
  createPromise(projectId: string, body: { title: string; type?: PromiseType; detail?: string | null; setupSceneId?: string | null; payoffSceneId?: string | null }): Promise<{ promise: PromiseView }> {
    return request(`/projects/${projectId}/promises`, { method: "POST", body: JSON.stringify(body) });
  },
  updatePromise(projectId: string, promiseId: string, body: { title?: string; type?: PromiseType; detail?: string | null; setupSceneId?: string | null; payoffSceneId?: string | null; status?: PromiseStatus; canonical?: number }): Promise<{ promise: PromiseView }> {
    return request(`/projects/${projectId}/promises/${promiseId}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  deletePromise(projectId: string, promiseId: string): Promise<{ success: boolean }> {
    return request(`/projects/${projectId}/promises/${promiseId}`, { method: "DELETE" });
  },
  extractPromises(projectId: string, model?: ModelChoice): Promise<{ result: { created: number; paidLinked: number; reviewed: number; chunks: number } } & PromisesResult> {
    return request(`/projects/${projectId}/promises/extract`, { method: "POST", body: JSON.stringify(model ?? {}) });
  },

  // ---- Revision Mode ----
  revisionPasses(projectId: string): Promise<{ passes: RevisionPass[] }> {
    return request(`/projects/${projectId}/revision`);
  },
  createRevisionPass(projectId: string, body: { title: string; goals?: string | null }): Promise<{ pass: RevisionPass }> {
    return request(`/projects/${projectId}/revision`, { method: "POST", body: JSON.stringify(body) });
  },
  updateRevisionPass(projectId: string, passId: string, body: { title?: string; goals?: string | null; status?: RevisionPassStatus }): Promise<{ pass: RevisionPass }> {
    return request(`/projects/${projectId}/revision/${passId}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  deleteRevisionPass(projectId: string, passId: string): Promise<{ success: boolean }> {
    return request(`/projects/${projectId}/revision/${passId}`, { method: "DELETE" });
  },
  revisionTasks(projectId: string, passId: string): Promise<{ tasks: RevisionTask[] }> {
    return request(`/projects/${projectId}/revision/${passId}/tasks`);
  },
  createRevisionTask(projectId: string, passId: string, body: { title: string; detail?: string | null; sceneId?: string | null; category?: RevisionTaskCategory }): Promise<{ task: RevisionTask }> {
    return request(`/projects/${projectId}/revision/${passId}/tasks`, { method: "POST", body: JSON.stringify(body) });
  },
  updateRevisionTask(taskId: string, body: { title?: string; detail?: string | null; status?: RevisionTaskStatus }): Promise<{ task: RevisionTask }> {
    return request(`/revision-tasks/${taskId}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  deleteRevisionTask(taskId: string): Promise<{ success: boolean }> {
    return request(`/revision-tasks/${taskId}`, { method: "DELETE" });
  },
  generateRevisionTasks(projectId: string, passId: string): Promise<{ added: number; tasks: RevisionTask[] }> {
    return request(`/projects/${projectId}/revision/${passId}/generate`, { method: "POST", body: JSON.stringify({}) });
  },
  reindex(projectId: string): Promise<{ result: unknown; insights: ProjectInsights }> {
    return request(`/projects/${projectId}/intelligence/reindex`, { method: "POST" });
  },
  summarizeScene(sceneId: string, model?: ModelChoice): Promise<{ summary: string | null }> {
    return request(`/scenes/${sceneId}/summarize`, { method: "POST", body: JSON.stringify(model ?? {}) });
  },
  analyzeScene(sceneId: string, model?: ModelChoice): Promise<{ scene: Scene | null; filled: string[] }> {
    return request(`/scenes/${sceneId}/analyze`, { method: "POST", body: JSON.stringify(model ?? {}) });
  },
  summarizeMissing(projectId: string, model?: ModelChoice): Promise<{ result: { summarized: number; remaining: number } }> {
    return request(`/projects/${projectId}/intelligence/summarize-scenes`, { method: "POST", body: JSON.stringify(model ?? {}) });
  },
  /** Start reading the whole book in the background (returns immediately). */
  startExtraction(projectId: string, model?: ModelChoice): Promise<{ started: boolean; total: number }> {
    return request(`/projects/${projectId}/intelligence/extract`, { method: "POST", body: JSON.stringify(model ?? {}) });
  },
  extractionStatus(projectId: string): Promise<{ job: ExtractionJob | null; provider: string | null; model: string | null; insights: ProjectInsights | null; intel: IntelStatus }> {
    return request(`/projects/${projectId}/intelligence/extract/status`);
  },
  knowledgeHealth(projectId: string): Promise<import("./types.js").KnowledgeHealth> {
    return request(`/projects/${projectId}/intelligence/knowledge-health`);
  },
  relationshipHistory(projectId: string): Promise<{ history: import("./types.js").RelationshipDimensionHistory[] }> {
    return request(`/projects/${projectId}/relationships/history`);
  },
  sceneStyle(sceneId: string): Promise<import("./types.js").StyleReport> {
    return request(`/scenes/${sceneId}/style`);
  },

  // ---- Relationships ----
  relationships(projectId: string): Promise<RelationshipsView> {
    return request(`/projects/${projectId}/relationships`);
  },
  createRelationship(
    projectId: string,
    input: { fromId: string; toId: string; kind: RelationshipKind; status: string; description?: string | null; sceneId?: string | null }
  ): Promise<RelationshipsView> {
    return request(`/projects/${projectId}/relationships`, { method: "POST", body: JSON.stringify(input) });
  },
  updateRelationship(
    projectId: string,
    relId: string,
    patch: { kind?: RelationshipKind; status?: string; description?: string | null; sceneId?: string | null }
  ): Promise<RelationshipsView> {
    return request(`/projects/${projectId}/relationships/${relId}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  approveRelationship(projectId: string, relId: string): Promise<RelationshipsView> {
    return request(`/projects/${projectId}/relationships/${relId}/approve`, { method: "POST" });
  },
  approveAllRelationships(projectId: string): Promise<RelationshipsView> {
    return request(`/projects/${projectId}/relationships/approve-all`, { method: "POST" });
  },
  dismissAllRelationships(projectId: string): Promise<RelationshipsView> {
    return request(`/projects/${projectId}/relationships/dismiss-all`, { method: "POST" });
  },
  deleteRelationship(projectId: string, relId: string): Promise<RelationshipsView> {
    return request(`/projects/${projectId}/relationships/${relId}`, { method: "DELETE" });
  },
  detectRelationships(projectId: string, model?: ModelChoice): Promise<RelationshipsView & { result: { created: number; skipped: number; chunks: number; truncated: boolean } }> {
    return request(`/projects/${projectId}/relationships/detect`, { method: "POST", body: JSON.stringify(model ?? {}) });
  },

  // ---- Continuity ----
  continuity(projectId: string): Promise<ContinuityView> {
    return request(`/projects/${projectId}/continuity`);
  },
  scanContinuity(projectId: string, model?: ModelChoice): Promise<ContinuityView & { result: { created: number; windows: number; truncated: boolean } }> {
    return request(`/projects/${projectId}/continuity/scan`, { method: "POST", body: JSON.stringify(model ?? {}) });
  },
  dismissContinuity(id: string): Promise<ContinuityView> {
    return request(`/continuity/${id}/dismiss`, { method: "POST" });
  },
  reopenContinuity(id: string): Promise<ContinuityView> {
    return request(`/continuity/${id}/reopen`, { method: "POST" });
  },

  // ---- Audiobook ----
  audiobook(projectId: string): Promise<AudiobookState> {
    return request(`/projects/${projectId}/audiobook`);
  },
  audiobookRendering(projectId: string): Promise<{ rendering: boolean }> {
    return request(`/projects/${projectId}/audiobook/rendering`);
  },
  renderAudiobook(
    projectId: string,
    opts: { voice?: string; scope?: "book" | "chapter"; chapterId?: string } = {}
  ): Promise<AudiobookState> {
    return request(`/projects/${projectId}/audiobook/render`, { method: "POST", body: JSON.stringify(opts) });
  },
  cancelAudiobook(projectId: string): Promise<{ canceled: boolean }> {
    return request(`/projects/${projectId}/audiobook/cancel`, { method: "POST" });
  },
  clearAudiobook(projectId: string): Promise<{ cleared: boolean; files: number; removed: number }> {
    return request(`/projects/${projectId}/audiobook/clear`, { method: "POST" });
  },
  audiobookTiming(projectId: string, opts: { scope?: "book" | "chapter"; chapterId?: string; voice?: string } = {}): Promise<AudiobookTiming> {
    const p = new URLSearchParams();
    if (opts.scope) p.set("scope", opts.scope);
    if (opts.chapterId) p.set("chapterId", opts.chapterId);
    if (opts.voice) p.set("voice", opts.voice);
    const qs = p.toString();
    return request(`/projects/${projectId}/audiobook/timing${qs ? `?${qs}` : ""}`);
  },
  reader(projectId: string): Promise<ReaderBook> {
    return request(`/projects/${projectId}/reader`);
  },

  // ---- Review / Markup Mode ----
  reviewAnnotations(
    projectId: string,
    opts: { scope?: "scene" | "chapter" | "project"; sceneId?: string; chapterId?: string } = {}
  ): Promise<{ annotations: ReviewAnnotation[] }> {
    const p = new URLSearchParams();
    if (opts.scope) p.set("scope", opts.scope);
    if (opts.sceneId) p.set("sceneId", opts.sceneId);
    if (opts.chapterId) p.set("chapterId", opts.chapterId);
    const qs = p.toString();
    return request(`/projects/${projectId}/review/annotations${qs ? `?${qs}` : ""}`);
  },
  createReviewAnnotation(
    projectId: string,
    body: {
      chapterId?: string | null;
      sceneId?: string | null;
      type: ReviewAnnotation["type"];
      category?: ReviewCategory | null;
      anchorType: ReviewAnnotation["anchorType"];
      anchor?: ReviewAnchor | null;
      selectedText?: string | null;
      note?: string | null;
    }
  ): Promise<{ annotation: ReviewAnnotation }> {
    return request(`/projects/${projectId}/review/annotations`, { method: "POST", body: JSON.stringify(body) });
  },
  updateReviewAnnotation(
    projectId: string,
    id: string,
    patch: {
      note?: string | null;
      category?: ReviewCategory | null;
      status?: ReviewAnnotation["status"];
      anchor?: ReviewAnchor | null;
      orphaned?: boolean;
    }
  ): Promise<{ annotation: ReviewAnnotation }> {
    return request(`/projects/${projectId}/review/annotations/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  deleteReviewAnnotation(projectId: string, id: string): Promise<{ deleted: boolean }> {
    return request(`/projects/${projectId}/review/annotations/${id}`, { method: "DELETE" });
  },
  reviewChapterScenes(projectId: string, chapterId: string): Promise<{ chapterTitle: string; scenes: { id: string; title: string; content: string }[] }> {
    return request(`/projects/${projectId}/review/chapter/${chapterId}/scenes`);
  },
  applyReviewRewrite(projectId: string, sceneId: string, content: string, changeSummary?: string): Promise<{ scene: Scene }> {
    return request(`/projects/${projectId}/review/scenes/${sceneId}/apply`, {
      method: "POST",
      body: JSON.stringify({ content, changeSummary })
    });
  },
  reviewStatuses(projectId: string): Promise<{ statuses: SceneReviewStatusEntry[] }> {
    return request(`/projects/${projectId}/review/statuses`);
  },
  setSceneReviewStatus(projectId: string, sceneId: string, status: SceneReviewStatus): Promise<{ status: SceneReviewStatusEntry }> {
    return request(`/projects/${projectId}/review/scenes/${sceneId}/status`, {
      method: "PUT",
      body: JSON.stringify({ status })
    });
  },
  audiobookAudioUrl(projectId: string, opts: { scope?: "book" | "chapter"; chapterId?: string; voice?: string } = {}): string {
    const p = new URLSearchParams();
    if (opts.scope) p.set("scope", opts.scope);
    if (opts.chapterId) p.set("chapterId", opts.chapterId);
    if (opts.voice) p.set("voice", opts.voice);
    const qs = p.toString();
    return `/api/projects/${projectId}/audiobook/audio${qs ? `?${qs}` : ""}`;
  },
  async downloadAudiobook(
    projectId: string,
    opts: { scope?: "book" | "chapter"; chapterId?: string; voice?: string } = {}
  ): Promise<void> {
    const params = new URLSearchParams();
    if (opts.scope) params.set("scope", opts.scope);
    if (opts.chapterId) params.set("chapterId", opts.chapterId);
    if (opts.voice) params.set("voice", opts.voice);
    const qs = params.toString();
    const response = await fetch(`/api/projects/${projectId}/audiobook/download${qs ? `?${qs}` : ""}`, { credentials: "same-origin" });
    if (!response.ok) throw new ApiError(response.status, "The audiobook isn't ready to download.");
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const match = /filename="([^"]+)"/.exec(disposition);
    const filename = match?.[1] ?? "audiobook.wav";
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  // ---- Export ----
  async downloadExport(
    projectId: string,
    format: ExportFormat,
    pdfOptions?: Record<string, string | number | boolean>
  ): Promise<void> {
    const params = new URLSearchParams({ format });
    if (format === "pdf" && pdfOptions) {
      for (const [key, value] of Object.entries(pdfOptions)) {
        params.set(key, typeof value === "boolean" ? (value ? "1" : "0") : String(value));
      }
    }
    const response = await fetch(`/api/projects/${projectId}/export?${params.toString()}`, {
      credentials: "same-origin"
    });
    if (!response.ok) {
      const text = await response.text();
      let message = `Export failed (${response.status})`;
      try {
        message = (JSON.parse(text) as { message?: string }).message ?? message;
      } catch {
        /* ignore */
      }
      throw new ApiError(response.status, message);
    }
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const match = /filename="([^"]+)"/.exec(disposition);
    const filename = match?.[1] ?? `manuscript.${format}`;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  // ---- Book Project (round-trip import/export) ----
  async downloadBookProject(projectId: string): Promise<void> {
    const response = await fetch(`/api/projects/${projectId}/export/book-project`, {
      credentials: "same-origin"
    });
    if (!response.ok) throw new ApiError(response.status, "Could not export the Book Project.");
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const match = /filename="([^"]+)"/.exec(disposition);
    const filename = match?.[1] ?? "book-project.zip";
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  importPreview(projectId: string, zipBase64: string): Promise<ImportPreview> {
    return request(`/projects/${projectId}/import/preview`, {
      method: "POST",
      body: JSON.stringify({ zipBase64 })
    });
  },
  importApply(projectId: string, zipBase64: string, acceptedSceneIds: string[]): Promise<ImportApplyResult> {
    return request(`/projects/${projectId}/import/apply`, {
      method: "POST",
      body: JSON.stringify({ zipBase64, acceptedSceneIds })
    });
  },
  listImports(projectId: string): Promise<{ imports: ProjectImportItem[] }> {
    return request(`/projects/${projectId}/imports`);
  },
  rollbackImport(projectId: string, importId: string): Promise<{ restored: number }> {
    return request(`/projects/${projectId}/imports/${importId}/rollback`, { method: "POST" });
  }
};

/** Read a File as base64 (no data: prefix) for Book Project upload. */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma === -1 ? result : result.slice(comma + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error("Could not read the file."));
    reader.readAsDataURL(file);
  });
}
